import React, { useRef, useState } from 'react'
import { EventValues } from '@sial/common-utils'
import { FormattedMessage } from 'react-intl'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import clsx from 'clsx'
import { useIntersectionObserver } from 'usehooks-ts'
import Carousel from 'nuka-carousel'
import {
  Theme,
  ButtonBase,
  Container,
  Typography,
  useMediaQuery,
} from '@material-ui/core'
import CarouselArrowLeftIcon from '@icons/CarouselArrowLeftIcon'
import CarouselArrowRightIcon from '@icons/CarouselArrowRightIcon'
import { DynamicProductFragment } from '@src/fragments/DynamicProduct.generated'
import DynamicProductCard from './DynamicProductCard'
import { sendCarouselPromoEvent } from '@src/utils/analytics/carouselAnalytics'
import {
  useCategorizePage,
  ProductCardTypeToPageMap,
} from '@utils/analytics/CategorizePage'
import useResponsiveSizes from '@utils/useResponsiveSizes'
import ProductQuickLook from '@src/components/ProductQuickLook/ProductQuickLook'
import {
  CarouselProduct,
  CarouselProductTypes,
  CAROUSEL_TITLE_LOOKUP_BY_TYPE,
  ProductCardProps,
} from './DynamicProductCardTypes'
import {
  handleSlideMovement,
  ProductCarouselControlProps,
} from './slideMovementHandler'

export const useStyles = makeStyles((theme: Theme) => {
  return {
    withoutControls: {
      padding: 0,
    },
    content: {
      paddingTop: theme.spacing(8),
      paddingBottom: theme.spacing(20),
      // Adds visible keyboard focus since the div element is tabbable (though not interactive)
      '& .slider-frame': {
        border: '2px solid transparent',
        '&:focus-visible': {
          border: `2px solid ${theme.palette.primary.main}`,
          borderRadius: theme.shape.borderRadius,
        },
      },
      [theme.breakpoints.up('sm')]: {
        paddingBottom: theme.spacing(8),
      },
    },
    contentInTab: {
      paddingTop: theme.spacing(4),
    },
    contentCondensed: {
      padding: 0,
    },
    navBtn: {},
    navBtnSmall: {
      height: 24,
      width: 24,
    },
    navBtnLarge: {
      height: 32,
      width: 32,
      display: 'none',
      [theme.breakpoints.up('sm')]: {
        display: 'flex',
      },
    },
    navBtnLeft: {
      transform: 'translateX(-40px)',
    },
    mobileBottomControls: {
      width: 105,
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
      bottom: -44,
      justifyContent: 'space-between',
      [theme.breakpoints.up('sm')]: {
        display: 'none',
      },
    },
    mobileBottomControlsHeroCard: {
      display: 'flex',
    },
    icon: {
      fontSize: theme.typography.pxToRem(14),
      color: theme.palette.primary.main,
      width: '1em',
    },
    iconDisabled: {
      color: theme.palette.grey[500],
    },
    cartRecommended: {
      backgroundColor: theme.palette.background.grey,
    },
    card: {
      [theme.breakpoints.up('sm')]: {
        paddingRight: theme.spacing(10),
      },
    },
    productHeroCardContent: {
      padding: 0,
      [theme.breakpoints.down('xs')]: {
        touchAction: 'pan-y',
      },
      '& $productHeroTile button': {
        '@media (min-width: 900px) and (max-width: 1300px)': {
          whiteSpace: 'normal',
        },
      },
    },
    productHeroTile: {
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(0, 4),
      },
    },
  }
})

const useTitleStyles = makeStyles((theme: Theme) => {
  return {
    contentCondensed: {
      padding: 0,
    },
    title: {
      marginBottom: theme.spacing(8),
      '$contentCondensed &': {
        marginBottom: theme.spacing(4),
      },
    },
    productHeroCardTitle: {
      margin: `${theme.spacing(
        0,
        0,
        theme.typography.pxToRem(40),
        0
      )} !important`,
      fontSize: `${theme.typography.pxToRem(18)} !important`,
      [theme.breakpoints.up('sm')]: {
        margin: `${theme.spacing(
          0,
          0,
          theme.typography.pxToRem(24),
          3
        )} !important`,
      },
    },
  }
})

interface ProductCarouselControlsConfig {
  productHeroCard?: boolean
  products: CarouselProduct[]
  creativeSlot: string
  pageType: string
  pageTypeDetails: string | null | undefined
}

export const CarouselTitle = ({
  title,
  isHero,
  type,
}: {
  title?: string | JSX.Element
  isHero: boolean
  type: CarouselProductTypes
}) => {
  const titleClasses = useTitleStyles()

  const renderTitle = (): React.ReactNode => {
    if (title) {
      return title
    }

    if (CAROUSEL_TITLE_LOOKUP_BY_TYPE[type]) {
      return <FormattedMessage {...CAROUSEL_TITLE_LOOKUP_BY_TYPE[type]} />
    }

    // Replaces the dashes and under scores in the type name with spaces for readability.
    // If the type is for some reason not found on the PRODUCT_CARD_TYPE_TRANSLATED_TITLE object.
    // Should never happen since it is type checked at build but APIs have burned us before.
    return type?.replace(/[-_]/g, ' ')
  }

  const titleContent = renderTitle()

  // If title is a JSX element, render it directly without Typography wrapper
  if (title && typeof title !== 'string') {
    return <>{titleContent}</>
  }

  // Otherwise, wrap in Typography
  return (
    <Typography
      variant="h2"
      className={clsx(
        titleClasses.title,
        isHero && titleClasses.productHeroCardTitle
      )}
    >
      {titleContent}
    </Typography>
  )
}
export const useProductCarouselControls = ({
  productHeroCard,
  products,
  creativeSlot,
  pageType,
  pageTypeDetails,
}: ProductCarouselControlsConfig) => {
  const classes = useStyles()

  return {
    renderCenterLeftControls: (props: ProductCarouselControlProps) => {
      const disabled = props.currentSlide === 0
      return (
        <>
          {!productHeroCard && (
            <ButtonBase
              className={clsx(
                classes.navBtn,
                classes.navBtnLarge,
                classes.navBtnLeft
              )}
              disabled={disabled}
              onClick={() =>
                handleSlideMovement(
                  props,
                  products,
                  'prev',
                  creativeSlot,
                  pageType,
                  pageTypeDetails
                )
              }
              aria-label="previous"
            >
              <CarouselArrowLeftIcon
                className={clsx(classes.icon, {
                  [classes.iconDisabled]: disabled,
                })}
              />
            </ButtonBase>
          )}
        </>
      )
    },
    renderCenterRightControls: (props: ProductCarouselControlProps) => {
      const disabled =
        props.currentSlide + props.slidesToShow === props.slideCount ||
        props.slideCount < props.slidesToShow
      return (
        <>
          {!productHeroCard && (
            <ButtonBase
              disabled={disabled}
              className={clsx(classes.navBtn, classes.navBtnLarge)}
              onClick={() =>
                handleSlideMovement(
                  props,
                  products,
                  'next',
                  creativeSlot,
                  pageType,
                  pageTypeDetails
                )
              }
              aria-label="next"
            >
              <CarouselArrowRightIcon
                className={clsx(classes.icon, {
                  [classes.iconDisabled]: props.slidesToScroll && disabled,
                })}
              />
            </ButtonBase>
          )}
        </>
      )
    },
    renderBottomCenterControls: (props: ProductCarouselControlProps) => {
      const previousDisabled = props.currentSlide === 0
      const nextDisabled =
        props.currentSlide + props.slidesToShow === props.slideCount ||
        props.slideCount < props.slidesToShow
      return (
        <div
          className={clsx(
            classes.mobileBottomControls,
            productHeroCard && classes.mobileBottomControlsHeroCard
          )}
        >
          <ButtonBase
            disabled={previousDisabled}
            onClick={() =>
              handleSlideMovement(
                props,
                products,
                'prev',
                creativeSlot,
                pageType,
                pageTypeDetails
              )
            }
            aria-label="previous"
            className={clsx(classes.navBtn, classes.navBtnSmall)}
          >
            <CarouselArrowLeftIcon
              className={clsx(classes.icon, {
                [classes.iconDisabled]: previousDisabled,
              })}
            />
          </ButtonBase>
          <Typography variant="body2">
            {Math.floor(props.currentSlide / props.slidesToShow) + 1} of{' '}
            {Math.round(props.slideCount / props.slidesToShow)}
          </Typography>
          <ButtonBase
            disabled={nextDisabled}
            onClick={() =>
              handleSlideMovement(
                props,
                products,
                'next',
                creativeSlot,
                pageType,
                pageTypeDetails
              )
            }
            aria-label="next"
            className={clsx(classes.navBtn, classes.navBtnSmall)}
          >
            <CarouselArrowRightIcon
              className={clsx(classes.icon, {
                [classes.iconDisabled]: props.slidesToScroll && nextDisabled,
              })}
            />
          </ButtonBase>
        </div>
      )
    },
  }
}

const DynamicProductCarousel: React.FC<ProductCardProps> = ({
  type,
  products,
  withoutControls,
  slidesToShow,
  condensed,
  preloadCardImages,
  title,
  showPdpRedesignLevelTwo,
  productHeroCard = false,
  isProductRecommendation = false,
  hideProductImg = false,
  displayDiscount,
  analyticsPageName = EventValues.NotAvailable,
  analyticsPageDetails,
}) => {
  const classes = useStyles()
  const getSize = useResponsiveSizes()

  const pageCategory = useCategorizePage()
  const creativeSlot = `${type} - ${pageCategory}`

  const carouselControls = useProductCarouselControls({
    productHeroCard,
    products,
    creativeSlot,
    pageType: analyticsPageName,
    pageTypeDetails: analyticsPageDetails,
  })

  const isCartRecommended =
    type === CarouselProductTypes.CartRecommendedProducts

  const [quickAddProduct, setQuickAddProduct] =
    useState<DynamicProductFragment | null>(null)

  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')) === true

  const carouselRef = useRef(null)
  const [sawCarousel, setSawCarousel] = useState(false)
  const carouselVisbility = useIntersectionObserver(carouselRef, {
    threshold: 0.66,
  })

  const productsByResponsiveSize = showPdpRedesignLevelTwo
    ? getSize({ xs: 1, sm: 2, md: 4, lg: 6 })
    : getSize({ xs: 1, sm: 2, md: 3, lg: 4 })

  const numProducts =
    productHeroCard && isDesktop ? 2 : slidesToShow || productsByResponsiveSize

  if (!products || products?.length === 0) return null

  if (carouselVisbility?.isIntersecting && !sawCarousel) {
    const slicedVisible = products.slice(0, numProducts)

    sendCarouselPromoEvent({
      creativeSlot,
      pageType: analyticsPageName,
      pageTypeDetails: analyticsPageDetails,
      products: slicedVisible,
    })
    setSawCarousel(true)
  }

  return (
    <div
      data-testid={type}
      id={type}
      className={clsx({
        [classes.cartRecommended]: isCartRecommended,
      })}
      ref={carouselRef}
    >
      <Container
        maxWidth="lg"
        className={clsx({
          [classes.withoutControls]: withoutControls,
        })}
      >
        <div
          className={clsx(
            classes.content,
            {
              [classes.contentCondensed]: condensed,
            },
            productHeroCard && classes.productHeroCardContent,
            isProductRecommendation && classes.contentInTab
          )}
        >
          <CarouselTitle title={title} isHero={productHeroCard} type={type} />
          <Carousel
            slidesToScroll={numProducts}
            slidesToShow={numProducts}
            disableEdgeSwiping
            withoutControls={withoutControls}
            {...carouselControls}
          >
            {products.map((product, i) => (
              <DynamicProductCard
                key={i}
                index={i}
                product={product}
                testId={type}
                onViewPrice={() => setQuickAddProduct(product)}
                preloadCardImages={preloadCardImages}
                className={clsx(
                  classes.card,
                  productHeroCard && classes.productHeroTile
                )}
                type={type}
                hideProductImg={hideProductImg}
                displayDiscount={displayDiscount}
              />
            ))}
          </Carousel>
        </div>
      </Container>

      {quickAddProduct && (
        <ProductQuickLook
          {...quickAddProduct}
          showAddToList
          open
          image={quickAddProduct.images[0]}
          onClose={() => setQuickAddProduct(null)}
          gaType={ProductCardTypeToPageMap[type]}
          erpType={quickAddProduct?.erp_type || []}
        />
      )}
    </div>
  )
}

export default DynamicProductCarousel
